import React from "react";
import { X } from "lucide-react";

import { AppDrawer } from "@/components/AppDrawer";
import { Button } from "@/components/Button";
import { Heading } from "@/components/Typography";
import { useDrawer } from "@/context/DrawerContext";
import { SessionPrep } from "@/pages/sessions/components/SessionPrepPage";
import { SessionSummary } from "@/pages/sessions/components/SessionSummaryPage";

/**
 * Global Session Prep / Session Summary drawers for the achiever — the same
 * two drawers the journey Sessions tab renders (see AchieverPabDetailPage),
 * hoisted into MainLayout so a notification can open one from any route
 * instead of navigating to /sessions.
 *
 * `programId` is the program the meeting actually belongs to, threaded into
 * SessionPrep/SessionSummary as `pabSessionId` so the meeting is fetched from
 * that program rather than from usePabSession()'s "active" one — which is a
 * different program whenever the notification came from an Expert Program or
 * an earlier PAB cycle.
 */
export const UseAchieverSessionDrawers: React.FC = () => {
  const {
    isAchieverSessionPrepDrawerOpen,
    achieverSessionPrepDrawerMeetingId,
    achieverSessionPrepDrawerProgramId,
    closeAchieverSessionPrepDrawer,
    isAchieverSessionSummaryDrawerOpen,
    achieverSessionSummaryDrawerMeetingId,
    achieverSessionSummaryDrawerProgramId,
    closeAchieverSessionSummaryDrawer,
  } = useDrawer();

  return (
    <>
      <AppDrawer
        isOpen={isAchieverSessionPrepDrawerOpen && achieverSessionPrepDrawerMeetingId !== null}
        onClose={closeAchieverSessionPrepDrawer}
        maxWidth="46rem"
        showCloseButton={false}
      >
        {achieverSessionPrepDrawerMeetingId !== null && (
          <div className="flex h-full flex-col">
            <div className="flex shrink-0 items-center justify-between border-b border-surface-border bg-surface-card px-6 py-4">
              <Heading
                as="h2"
                className="text-[20px] font-bold leading-7 tracking-[-0.45px] text-goals-create-title"
              >
                Session Prep
              </Heading>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Close"
                onClick={closeAchieverSessionPrepDrawer}
                className="rounded-full p-2 text-theme-text-muted hover:bg-surface-card-inner"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="min-h-0 flex-1">
              <SessionPrep
                meetingId={achieverSessionPrepDrawerMeetingId}
                onClose={closeAchieverSessionPrepDrawer}
                pabSessionId={achieverSessionPrepDrawerProgramId ?? undefined}
                variant="drawer"
              />
            </div>
          </div>
        )}
      </AppDrawer>

      <AppDrawer
        isOpen={isAchieverSessionSummaryDrawerOpen && achieverSessionSummaryDrawerMeetingId !== null}
        onClose={closeAchieverSessionSummaryDrawer}
        maxWidth="46rem"
        showCloseButton={false}
      >
        {achieverSessionSummaryDrawerMeetingId !== null && (
          <div className="flex h-full flex-col">
            <div className="flex shrink-0 items-center justify-between border-b border-surface-border bg-surface-card px-6 py-4">
              <Heading
                as="h2"
                className="text-[20px] font-bold leading-7 tracking-[-0.45px] text-goals-create-title"
              >
                Session Summary
              </Heading>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Close"
                onClick={closeAchieverSessionSummaryDrawer}
                className="rounded-full p-2 text-theme-text-muted hover:bg-surface-card-inner"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="min-h-0 flex-1">
              <SessionSummary
                meetingId={achieverSessionSummaryDrawerMeetingId}
                onClose={closeAchieverSessionSummaryDrawer}
                status="completed"
                pabSessionId={achieverSessionSummaryDrawerProgramId ?? undefined}
              />
            </div>
          </div>
        )}
      </AppDrawer>
    </>
  );
};
